import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save } from 'lucide-react';
import { doc, getDoc, updateDoc, collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import ImageUpload from '../../components/ui/ImageUpload';

const EditPlayerPage = () => {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    playtomicRating: '',
    position: '',
    key: '',
    playingHand: '',
    gamesPlayed: '',
    category: '',
    remark: '',
    teamId: '',
    teamName: ''
  });
  const [profileImage, setProfileImage] = useState('');
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPlayer = async () => {
      try {
        const playerDoc = await getDoc(doc(db, 'players', playerId));
        if (playerDoc.exists()) {
          const playerData = { id: playerDoc.id, ...playerDoc.data() };
          setPlayer(playerData);
          setFormData({
            name: playerData.name || '',
            playtomicRating: playerData.playtomicRating || '',
            position: playerData.position || '',
            key: playerData.key || '',
            playingHand: playerData.playingHand || '',
            gamesPlayed: playerData.gamesPlayed || '',
            category: playerData.category || '',
            remark: playerData.remark || '',
            teamId: playerData.teamId || '',
            teamName: playerData.teamName || ''
          });
          setProfileImage(playerData.profileImage || '');
        } else {
          setError('Player not found');
        }
      } catch (err) {
        setError('Failed to load player details');
        console.error('Error fetching player:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchPlayer();
  }, [playerId]);

  useEffect(() => {
    const teamsQuery = query(
      collection(db, 'teams'),
      orderBy('name', 'asc')
    );

    const unsubscribe = onSnapshot(teamsQuery, (snapshot) => {
      const teamsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setTeams(teamsData);
    });

    return unsubscribe;
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTeamChange = (e) => {
    const teamId = e.target.value;
    const selectedTeam = teams.find(team => team.id === teamId);
    setFormData(prev => ({
      ...prev,
      teamId: teamId,
      teamName: selectedTeam ? selectedTeam.name : ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    if (!formData.name.trim()) {
      setError('Player name is required.');
      setSaving(false);
      return;
    }

    try {
      const updateData = {
        name: formData.name,
        playtomicRating: formData.playtomicRating ? parseFloat(formData.playtomicRating) : null,
        position: formData.position,
        key: formData.key,
        playingHand: formData.playingHand,
        gamesPlayed: formData.gamesPlayed ? parseInt(formData.gamesPlayed) : null,
        category: formData.category,
        remark: formData.remark,
        teamId: formData.teamId || null,
        teamName: formData.teamName,
        profileImage: profileImage,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'players', playerId), updateData);

      navigate('/players');
    } catch (error) {
      setError('Failed to update player. Please try again.');
      console.error('Error updating player:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="loading">Loading player details...</div>
      </div>
    );
  }

  if (error && !player) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#1C563D' }}>
            {error}
          </h2>
          <button
            onClick={() => navigate('/players')}
            className="btn btn-primary"
          >
            Back to Players
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={() => navigate('/players')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-4"
        >
          <ArrowLeft size={20} />
          Back to Players
        </button>
        <h1 className="text-3xl font-bold mb-2" style={{ color: '#1C563D' }}>
          Edit Player
        </h1>
        <p className="text-gray-600">
          Update player information and team assignment
        </p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="card max-w-2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="form-group">
              <label htmlFor="name" className="form-label">
                Player Name *
              </label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="form-input"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter player name"
              />
            </div>

            <div className="form-group">
              <ImageUpload
                currentImageUrl={profileImage}
                onImageChange={setProfileImage}
                folder="player-profiles"
                aspectRatio="portrait"
                className="mb-6"
              />
            </div>

            <div className="form-group">
              <label htmlFor="playtomicRating" className="form-label">
                Playtomic Rating
              </label>
              <input
                id="playtomicRating"
                name="playtomicRating"
                type="number"
                step="0.01"
                className="form-input"
                value={formData.playtomicRating}
                onChange={handleInputChange}
                placeholder="e.g., 3.5"
              />
            </div>

            <div className="form-group">
              <label htmlFor="position" className="form-label">
                Position
              </label>
              <select
                id="position"
                name="position"
                className="form-input"
                value={formData.position}
                onChange={handleInputChange}
              >
                <option value="">Select Position</option>
                <option value="Left">Left</option>
                <option value="Right">Right</option>
                <option value="Both">Both</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="playingHand" className="form-label">
                Playing Hand
              </label>
              <select
                id="playingHand"
                name="playingHand"
                className="form-input"
                value={formData.playingHand}
                onChange={handleInputChange}
              >
                <option value="">Select Hand</option>
                <option value="Left">Left</option>
                <option value="Right">Right</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <select
                id="category"
                name="category"
                className="form-input"
                value={formData.category}
                onChange={handleInputChange}
              >
                <option value="">Select Category</option>
                <option value="Tier 1">Tier 1</option>
                <option value="Tier 2">Tier 2</option>
                <option value="Tier 3">Tier 3</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="gamesPlayed" className="form-label">
                Games Played
              </label>
              <input
                id="gamesPlayed"
                name="gamesPlayed"
                type="number"
                className="form-input"
                value={formData.gamesPlayed}
                onChange={handleInputChange}
                placeholder="e.g., 150"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="teamId" className="form-label">
              Team Assignment
            </label>
            <select
              id="teamId"
              name="teamId"
              className="form-input"
              value={formData.teamId}
              onChange={handleTeamChange}
            >
              <option value="">No team assigned</option>
              {teams.map(team => (
                <option key={team.id} value={team.id}>
                  {team.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="key" className="form-label">
              Key
            </label>
            <input
              id="key"
              name="key"
              type="text"
              className="form-input"
              value={formData.key}
              onChange={handleInputChange}
              placeholder="e.g., Over 40, Under 18, Foreign"
            />
          </div>

          <div className="form-group">
            <label htmlFor="remark" className="form-label">
              Remark
            </label>
            <textarea
              id="remark"
              name="remark"
              className="form-input"
              rows="3"
              value={formData.remark}
              onChange={handleInputChange}
              placeholder="Additional notes about the player"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => navigate('/players')}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary flex-1"
              style={{
                backgroundColor: saving ? '#999' : '#1C563D',
                cursor: saving ? 'not-allowed' : 'pointer'
              }}
            >
              {saving ? 'Saving Changes...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default EditPlayerPage;
